import { PrimarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import BookingDetailsDialog from 'features/BookingDetailsDialog'
import BookingsDatepicker from 'features/GDSBookings/Datepicker'
import useResortNearDisney from 'hooks/useResortNearDisney'
import moment from 'moment'
import { useEffect, useState } from 'react'
import bookingsApi from 'services/api/bookings'
import { formatPrice } from 'utils/common'

import styles from './Bookings.module.scss'
import { ReactComponent as WorldIcon } from './world.svg'

const bookingStatusTypes = {
  Completed: 'Completed',
  InUse: 'In Use',
  NoShow: 'No Show',
  Confirmed: 'Confirmed',
  Cancelled: 'Cancelled',
  WaitListed: 'Wait Listed',
  Rejected: 'Rejected',
  Pending: 'Pending',
  RefundPending: 'Pending refund'
}

const GDSBookings = () => {
  const [isBookingsLoading, setIsBookingsLoading] = useState(true)
  const [isBookingsErred, setIsBookingsErred] = useState(false)

  const [bookings, setBookings] = useState([])

  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [checkIn, setCheckIn] = useState()
  const [checkOut, setCheckOut] = useState()

  const [bookingStartDate, setBookingStartDate] = useState(moment().add('-14', 'days'))
  const [bookingEndDate, setBookingEndDate] = useState(moment())

  const [bookingStatus, setBookingStatus] = useState(null)
  const [activeBookingId, setActiveBookingId] = useState()

  const [sortIndex, setSortIndex] = useState(4)
  const [sortAscending, setSortAscending] = useState(false)

  const [isBookingDetailsDialogVisible, setIsBookingDetailsDialogVisible] =
    useState(false)

  const isResortNearDisney = useResortNearDisney()

  function handleBookingClick(bookingId) {
    setActiveBookingId(bookingId)
    setIsBookingDetailsDialogVisible(true)
  }

  function handleSearch() {
    setIsBookingsLoading(true)
    setIsBookingsErred(false)

    setBookings([])

    bookingsApi
      .list({
        allSubsidiary: true,
        isGds: bookingStatus === 'Pending' ? false : true,
        firstName,
        lastName,
        checkIn,
        checkOut,
        bookingStartDate: bookingStartDate.format('YYYY-MM-DD'),
        bookingEndDate: bookingEndDate.format('YYYY-MM-DD'),
        bookingStatus: bookingStatus === 'null' ? null : bookingStatus,
        subsidiaryId: isResortNearDisney ? '08db52e8-9d6e-4242-89f0-9737b0b112fa' : null // business logic
      })
      .then(bookings => {
        setBookings(bookings)
        setIsBookingsErred(false)
      })
      .catch(() => {
        setIsBookingsErred(true)
      })
      .finally(() => {
        setIsBookingsLoading(false)
      })
  }

  function handleTitleClick(i) {
    const sortableIndexes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

    if (sortIndex === i) {
      setSortAscending(d => !d)
    }

    if (sortableIndexes.includes(i)) {
      setSortIndex(i)
    }
  }

  function sortResults(results) {
    const sortedData = results.sort((a, b) => {
      switch (sortIndex) {
        case 0:
          return a.hotelName.localeCompare(b.hotelName)

        case 1:
          return a.fullName.localeCompare(b.fullName)

        case 2:
          return new Date(a.checkIn) - new Date(b.checkIn)

        case 3:
          return new Date(a.checkOut) - new Date(b.checkOut)

        case 4:
          return new Date(a.bookingDate) - new Date(b.bookingDate)

        case 5:
          return a.bookingStatus - b.bookingStatus

        case 6:
          return a.nightlyRate - b.nightlyRate

        case 7:
          return a.totalRate - b.totalRate

        case 8:
          if (a.bookingSource === null) {
            return -1
          }

          return a.bookingSource?.localeCompare(b?.bookingSource)

        case 9:
          if (a.trackingInfo === null) {
            return -1
          }

          return a.trackingInfo?.localeCompare(b?.trackingInfo)

        default:
          return 0
      }
    })

    return sortAscending ? sortedData : sortedData.reverse()
  }

  useEffect(() => {
    setIsBookingsLoading(true)

    bookingsApi
      .list({
        allSubsidiary: true,
        isGds: true,
        bookingStatus: null,
        subsidiaryId: isResortNearDisney ? '08db52e8-9d6e-4242-89f0-9737b0b112fa' : null // business logic
      })
      .then(bookings => {
        setBookings(bookings)
        setIsBookingsErred(false)
      })
      .catch(() => {
        setIsBookingsErred(true)
      })
      .finally(() => {
        setIsBookingsLoading(false)
      })
  }, [isResortNearDisney])

  function handleInputKeyPress(event) {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <div>
      {isBookingDetailsDialogVisible && (
        <BookingDetailsDialog
          onClose={() => setIsBookingDetailsDialogVisible(false)}
          bookingId={activeBookingId}
        />
      )}
      <CustomHeader title="Bookings" width={1080} isFixed />
      <div className={styles.wrapper}>
        <div className={styles.bar}>
          <div className={styles.container}>
            <div className={styles.filters}>
              <TextInput
                className={styles.textInput}
                label="Last name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                type="Search"
                onKeyPress={handleInputKeyPress}
              />
              <TextInput
                className={styles.textInput}
                label="First name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                type="Search"
                onKeyPress={handleInputKeyPress}
              />
              <BookingsDatepicker
                label="Check-in"
                selected={checkIn ? moment(checkIn).toDate() : null}
                onChange={date =>
                  date ? setCheckIn(moment(date).format('YYYY/MM/DD')) : setCheckIn()
                }
              />
              <BookingsDatepicker
                label="Check-out"
                selected={checkOut ? moment(checkOut).toDate() : null}
                onChange={date =>
                  date ? setCheckOut(moment(date).format('YYYY/MM/DD')) : setCheckOut()
                }
              />
              <div>
                <p className={styles.bookingDateLabel}>Booking dates</p>
                <SmartDatepicker
                  className={styles.bookingDates}
                  inputClassName={styles.bookingDatesInput}
                  startDate={bookingStartDate}
                  endDate={bookingEndDate}
                  onChangeStartDate={setBookingStartDate}
                  onChangeEndDate={setBookingEndDate}
                  showMonths
                  showQuarters
                  smartDates={[
                    smartDateTypes.last7days,
                    smartDateTypes.last14days,
                    smartDateTypes.last30days,
                    smartDateTypes.thisWeek
                  ]}
                />
              </div>
              <Dropdown
                label="Status"
                className={styles.dropdown}
                value={bookingStatus}
                onChange={e => setBookingStatus(e.target.value)}
                options={{ ...bookingStatusTypes, null: 'All' }}
              />
              <PrimarySmallButton className={styles.searchButton} onClick={handleSearch}>
                Search
              </PrimarySmallButton>
            </div>
          </div>
        </div>
        {isBookingsLoading && (
          <div className={styles.wrapper}>
            <LoadingIndicator longWaitingTreshold={120} />
          </div>
        )}
        {isBookingsErred && (
          <p className={styles.noResults}>
            Something went wrong while getting the bookings
          </p>
        )}
        {!isBookingsErred && !isBookingsLoading && !bookings && (
          <p className={styles.noResults}>No bookings found for selected filters</p>
        )}
        {!isBookingsLoading && bookings?.length > 0 && (
          <Table
            widths={[220, 140, 55, 55, 100, 90, 80, 100, 100]}
            handleTitleCellClick={handleTitleClick}
            sortIndex={sortIndex}
            sortDirection={sortAscending ? 'up' : 'down'}
          >
            <TitleRow>
              <TitleCell>Hotel name</TitleCell>
              <TitleCell>Guest name</TitleCell>
              <TitleCell>C/I</TitleCell>
              <TitleCell>C/O</TitleCell>
              <TitleCell>Booking date</TitleCell>
              <TitleCell>Status</TitleCell>
              <TitleCell>Nightly rate</TitleCell>
              <TitleCell>Total rate</TitleCell>
              <TitleCell>Source</TitleCell>
              <TitleCell>Tracking info</TitleCell>
            </TitleRow>
            {sortResults(bookings).map(b => (
              <Row className={styles.row} key={b.bookingId}>
                <Cell className={styles.hotelNameCell}>
                  {b.bookingScope === 'WorldWideBooking' && (
                    <WorldIcon className={styles.worldwideIcon} />
                  )}
                  {b.hotelName}
                </Cell>
                <Cell className={styles.fullNameCell}>
                  <button
                    className={styles.fullNameButton}
                    onClick={() => handleBookingClick(b.bookingId)}
                  >
                    {b.fullName}
                  </button>
                </Cell>
                <Cell>{b.checkIn ? moment(b.checkIn).format('MMM D') : '-'}</Cell>
                <Cell>{b.checkOut ? moment(b.checkOut).format('MMM D') : '-'}</Cell>
                <Cell>
                  {b.bookingDate ? moment(b.bookingDate).format('MMM D, YYYY') : '-'}
                </Cell>
                <Cell>
                  <span
                    style={{
                      fontWeight:
                        b.bookingStatus === bookingStatusTypes.Pending ? '600' : 'normal',
                      color:
                        b.bookingStatus === bookingStatusTypes.Pending ? 'red' : '#646a64'
                    }}
                  >
                    {bookingStatusTypes.Cancelled && b.refundStatus === 'Completed'
                      ? 'Refunded'
                      : bookingStatusTypes[b.bookingStatus]}
                  </span>
                </Cell>
                <Cell>{formatPrice(b.nightlyRate)}</Cell>
                <Cell>{formatPrice(b.totalRate)}</Cell>
                <Cell overflow="hidden">
                  <div title={b.bookingSource} className={styles.overflowHolder}>
                    {b.bookingSource}
                  </div>
                </Cell>
                <Cell>
                  <div className={styles.overflowHolder} title={b.trackingInfo}>
                    {b.trackingInfo}
                  </div>
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </div>
    </div>
  )
}

export default GDSBookings
